import React from 'react';

import { type CURRENCY } from '@nfctron/nexus-core';

import { RadioGroup } from '@root/features/form/components/RadioGroup/RadioGroup';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';

import { CartStoreExtraProductsSlice } from '../../stores/cartStore';

/**
 * ProductVariantController component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const ProductVariantController: React.FunctionComponent<ProductVariantControllerProps> = ({
	currencyId,
	onChange,
	value,
	productGroupVariantControlType,
	options,
}) => {
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, ProductVariantController.displayName);

	/** render controller node based on the control type provided */
	const controllerNode = React.useMemo(() => {
		switch (productGroupVariantControlType) {
			// TODO: support other control types
			case CartStoreExtraProductsSlice.PRODUCT_GROUP_VARIANT_CONTROL_TYPE.SLIDER:
			case CartStoreExtraProductsSlice.PRODUCT_GROUP_VARIANT_CONTROL_TYPE.DROPDOWN:
			case CartStoreExtraProductsSlice.PRODUCT_GROUP_VARIANT_CONTROL_TYPE.RADIO:
				return (
					<RadioGroup
						data={options.map(({ value: optionValue, foundProductMeta }) => ({
							/** variant name */
							label: optionValue.name,
							/** quantity limited variant */
							...(foundProductMeta?.quantityLimited && {
								disabled: true,
								description: t('quantityLimitedOption.text'),
							}),
							/** sold out variant */
							...(foundProductMeta?.soldOut && {
								disabled: true,
								description: t('soldOutOption.text'),
							}),
							value: optionValue.valueId.toString(),
						}))}
						value={value?.toString()}
						onChange={onChange}
					/>
				);
			default:
				return null;
		}
	}, [onChange, options, productGroupVariantControlType, t, value]);

	return <React.Fragment>{controllerNode}</React.Fragment>;
};

/**
 * ProductVariantController component option type
 * @export
 */
export type ProductVariantControllerOption = {
	value: CartStoreExtraProductsSlice.ExtraProductGroupVariantValue;
	newCombinations: CartStoreExtraProductsSlice.ExtraProductConfigVariantCombo;
	possibleProducts: CartStoreExtraProductsSlice.ExtraProductConfig[];
	foundProduct: CartStoreExtraProductsSlice.ExtraProductConfig | null;
	foundProductMeta:
		| {
				quantityLimited: boolean;
				soldOut: boolean;
		  }
		| undefined;
	isActive: boolean;
};

/**
 * ProductVariantController component props type
 * @export
 */
export type ProductVariantControllerProps = {
	productGroupVariantControlType: CartStoreExtraProductsSlice.PRODUCT_GROUP_VARIANT_CONTROL_TYPE;
	options: ProductVariantControllerOption[];
	value: number | null;
	onChange: (value: number | string) => void;
	currencyId: CURRENCY;
};

/**
 * ProductVariantController component display name
 * @type {ComponentDisplayName}
 */
ProductVariantController.displayName = 'Event/Cart/ProductVariantController';
